import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AgendaCard from '../components/AgendaCard';
// import EventCardHorizontal from '../components/EventCardHorizontal';
import Button from '../components/Button/Button';
import Typography from '../components/Typography';
import { useEventContext } from '../context/EventContext';
import { useAuth } from '../context/AuthContext';
import { theme } from '../theme';
import AgendaFilters from '../components/AgendaFilters';
import MainNavbar from '../components/Navbar/MainNavbar';
import { TICKET_FREE, TICKET_PAID } from '../constants/eventTypes';
import SpinnerCircular from '../components/SpinnerCircular';

const Agenda = () => {
  const { currentViewEvents, currentView, changeView, applyFilters, viewFilters, loading } = useEventContext();
  const { user } = useAuth();
  const { t } = useTranslation('agenda');
  const navigate = useNavigate();

  // Estados para búsqueda y filtros secundarios (independientes por vista)
  const [searchTerm, setSearchTerm] = useState('');

  // Obtener filtros para la vista actual
  const getCurrentViewFilters = () => {
    return viewFilters[currentView] || {
      enrollmentStatus: [],
      eventType: [],
      vehicle: '',
      terrain: [],
      price: [],
      difficulty: [],
      showOrganizedEvents: true
    };
  };

  // Estado local para filtros secundarios (sincronizado con el contexto)
  const [secondaryFilters, setSecondaryFilters] = useState(getCurrentViewFilters());

  // Sincronizar filtros locales cuando cambia la vista
  useEffect(() => {
    setSecondaryFilters(getCurrentViewFilters());
  }, [currentView, viewFilters]);

  const handleCreateEvent = () => {
    navigate('/create-event');
  };

  const handleExploreEvents = () => {
    navigate('/discover');
  };

  // Función para determinar el rol del usuario en un evento
  const getUserRoleInEvent = (event) => {
    if (!user || !event) return null;

    // Verificar si es organizador
    const isOrganizer = event.owner?._id === user.id || event.owner?.id === user.id;

    // Verificar si está inscrito
    const isEnrolled = event.attendees?.some(attendee => {
      const attendeeUserId = attendee.userId?._id || attendee.userId?.id || attendee.userId;
      return attendeeUserId?.toString() === user.id?.toString();
    });

    if (isOrganizer && isEnrolled) return 'both';
    if (isOrganizer) return 'organizing';
    if (isEnrolled) return 'enrolled';
    return null;
  };

  // Obtener eventos de la vista actual
  const getCurrentViewEvents = () => {
    if (!currentViewEvents) return [];

    let allEvents = [];

    // Siempre incluir eventos inscritos
    allEvents = [...(currentViewEvents.enrolled || [])];

    // Incluir eventos organizados solo si showOrganizedEvents es true
    if (secondaryFilters.showOrganizedEvents) {
      allEvents = [
        ...allEvents,
        ...(currentViewEvents.organized || [])
      ];
    }

    // Ordenar por fecha
    const sortOrder = currentView === 'past' ? -1 : 1;
    allEvents.sort((a, b) => sortOrder * (new Date(a.date) - new Date(b.date)));

    return allEvents;
  };

  // Función para obtener el estado de inscripción del usuario en un evento
  const getUserEnrollmentStatus = (event) => {
    if (!user || !event.attendees || !Array.isArray(event.attendees)) {
      return null;
    }

    const userAttendee = event.attendees.find(attendee => {
      const attendeeUserId = attendee.userId?._id || attendee.userId?.id || attendee.userId;
      return attendeeUserId?.toString() === user.id?.toString();
    });

    return userAttendee?.status || null;
  };

  // Función para filtrar eventos (solo filtros secundarios, aplicados localmente)
  const filterEvents = (events) => {
    return events.filter(event => {
      // Filtro de búsqueda por nombre del evento u organizador
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const eventNameMatch = event.title?.toLowerCase().includes(searchLower);
        const organizerMatch = event.owner?.name?.toLowerCase().includes(searchLower) ||
          event.owner?.firstName?.toLowerCase().includes(searchLower) ||
          event.owner?.lastName?.toLowerCase().includes(searchLower);

        if (!eventNameMatch && !organizerMatch) {
          return false;
        }
      }

      // Filtro por estado de inscripción
      if (secondaryFilters.enrollmentStatus && secondaryFilters.enrollmentStatus.length > 0) {
        const userStatus = getUserEnrollmentStatus(event);
        if (!userStatus || !secondaryFilters.enrollmentStatus.includes(userStatus)) {
          return false;
        }
      }

      // Filtro por tipo de evento
      if (secondaryFilters.eventType && secondaryFilters.eventType.length > 0) {
        if (!secondaryFilters.eventType.includes(event.eventType)) {
          return false;
        }
      }

      // Filtro por vehículo (solo eventos donde el usuario está inscrito con ese vehículo)
      if (secondaryFilters.vehicle) {
        const userAttendee = event.attendees?.find(attendee => {
          const attendeeUserId = attendee.userId?._id || attendee.userId?.id || attendee.userId;
          return attendeeUserId?.toString() === user.id?.toString();
        });

        if (!userAttendee || userAttendee.vehicleId !== secondaryFilters.vehicle) {
          return false;
        }
      }

      // Filtro por tipo de terreno
      if (secondaryFilters.terrain && secondaryFilters.terrain.length > 0) {
        const eventTerrain = Array.isArray(event.terrain) ? event.terrain : [event.terrain];
        const hasMatchingTerrain = eventTerrain.some(terrain => secondaryFilters.terrain.includes(terrain));
        if (!hasMatchingTerrain) {
          return false;
        }
      }

      // Filtro por precio
      if (secondaryFilters.price && secondaryFilters.price.length > 0) {
        const isFree = !event.price || event.price === 0;
        const isPaid = event.price && event.price > 0;

        if (secondaryFilters.price.includes(TICKET_FREE) && !isFree) {
          return false;
        }
        if (secondaryFilters.price.includes(TICKET_PAID) && !isPaid) {
          return false;
        }
        if (!secondaryFilters.price.includes(TICKET_FREE) && !secondaryFilters.price.includes(TICKET_PAID)) {
          return false;
        }
      }

      // Filtro por dificultad
      if (secondaryFilters.difficulty && secondaryFilters.difficulty.length > 0) {
        if (!secondaryFilters.difficulty.includes(event.difficulty)) {
          return false;
        }
      }

      return true;
    });
  };

  // Eventos filtrados y procesados
  const filteredEvents = useMemo(() => {
    const viewEvents = getCurrentViewEvents();
    return filterEvents(viewEvents);
  }, [currentViewEvents, searchTerm, secondaryFilters, user, currentView]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
  };

  // Manejar cambio de vista (navegación principal)
  const handleViewChange = (newView) => {
    changeView(newView);
  };

  // Función para manejar cambios en filtros secundarios
  const handleSecondaryFiltersChange = (newFilters) => {
    setSecondaryFilters(newFilters);
    applyFilters(newFilters);
  };

  return (
    <>
      <MainNavbar />
      <AgendaContainer>
        <SectionHeader>
          <Container>
            <HeaderInner>
              <HeaderBlock>
                <Typography as="h1" $variant="title-4-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold">
                  {t('header.title')}
                </Typography>
                <Typography as="p" $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
                  {t('header.description')}
                </Typography>
              </HeaderBlock>
            </HeaderInner>
          </Container>
        </SectionHeader>
        <Container>
          {/* Bloque de búsqueda y filtros */}
          <SearchSection>
            <AgendaFilters
              currentView={currentView}
              onViewChange={handleViewChange}
              secondaryFilters={secondaryFilters}
              onSecondaryFiltersChange={handleSecondaryFiltersChange}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchClear={handleSearchClear}
              currentViewEvents={currentViewEvents}
            />
          </SearchSection>

          {/* Listado de eventos */}
          <EventsSection>
            {loading ? (
              <LoadingContainer>
                <SpinnerCircular
                  size={30}
                  thickness={180}
                  speed={100}
                  color={theme.fill.brandMain}
                  secondaryColor={theme.fill.defaultWeak}
                />
              </LoadingContainer>
            ) : filteredEvents && filteredEvents.length > 0 ? (
              <List>
                {filteredEvents.map((event) => (
                  // <EventCardHorizontal key={event.id} event={event} />
                  <AgendaCard key={event.id} event={event} />
                ))}
              </List>
            ) : (
              <EmptyState>
                <EmptyStateHeader>
                  <Typography as="p" $variant="title-4-semibold" color={theme.colors.defaultMain}>
                    {searchTerm || Object.values(secondaryFilters).some(f => Array.isArray(f) ? f.length > 0 : (f && f !== true))
                      ? t('all.emptyState.noResults')
                      : t('all.emptyState.title')}
                  </Typography>
                  <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultWeak}>
                    {searchTerm || Object.values(secondaryFilters).some(f => Array.isArray(f) ? f.length > 0 : (f && f !== true))
                      ? t('all.emptyState.noResultsDescription')
                      : t('all.emptyState.description')}
                  </Typography>
                </EmptyStateHeader>
                {!searchTerm && !Object.values(secondaryFilters).some(f => Array.isArray(f) ? f.length > 0 : (f && f !== true)) && (
                  <EmptyStateActions>
                    <Button $variant="brand" onClick={handleCreateEvent}>
                      {t('all.emptyState.createEvent')}
                    </Button>
                    <Button $variant="defaultInverse" onClick={handleExploreEvents}>
                      {t('all.emptyState.exploreEvents')}
                    </Button>
                  </EmptyStateActions>
                )}
              </EmptyState>
            )}
          </EventsSection>
        </Container>
      </AgendaContainer>
    </>
  );
};

export default Agenda;

const AgendaContainer = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  padding: 0 ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 ${({ theme }) => theme.sizing.sm};
  }
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 16px;
  }
`;

const HeaderInner = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  width: 100%;
  padding: ${({ theme }) => theme.sizing.sm} 0px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: space-between;
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-grow: 1;
    gap: ${({ theme }) => theme.sizing.xxs};
  }
`;

const SearchSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
  padding-top: ${({ theme }) => theme.sizing.sm};
  padding-bottom: ${({ theme }) => theme.sizing.lg};
`;

const EventsSection = styled.div`
  width: 100%;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const EmptyState = styled.div`
  align-items: center;
  align-self: stretch;
  background-color: ${theme.colors.backgroundWeak};
  border-radius: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: center;
  padding: 80px ${({ theme }) => theme.sizing.sm};;
`;

const EmptyStateHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};;
`;

const EmptyStateActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  max-width: 280px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.sizing.xl};
`;
